import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { isLineOaJoined, markLineOaJoined } from '../../../lib/lineOa';

interface Props {
  onClose: () => void;
  initialStep?: number;
}

const STEPS = [
  { id: 1, label: '登入帳號', icon: 'ri-user-check-line' },
  { id: 2, label: '上傳物件', icon: 'ri-upload-cloud-2-line' },
  { id: 3, label: '加入機器人', icon: 'ri-robot-2-line' },
  { id: 4, label: '智能發文', icon: 'ri-megaphone-line' },
];

export default function LandlordOnboardingModal({ onClose, initialStep = 1 }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [uploadVisited, setUploadVisited] = useState(initialStep > 2);
  const [botConfirmed, setBotConfirmed] = useState(() => {
    if (initialStep > 3) return true;
    return isLineOaJoined();
  });
  const [autoPostConfirmed, setAutoPostConfirmed] = useState(() => {
    if (initialStep > 4) return true;
    return localStorage.getItem('blueshelter_autopost_confirmed') === '1';
  });

  useEffect(() => {
    if (botConfirmed) markLineOaJoined();
  }, [botConfirmed]);

  useEffect(() => {
    if (autoPostConfirmed) localStorage.setItem('blueshelter_autopost_confirmed', '1');
  }, [autoPostConfirmed]);

  const completedSteps = STEPS.map((s) => s.id).filter((id) => step > id);

  const isComplete = step === 5;

  const handleGoPublish = () => {
    setUploadVisited(true);
    onClose();
    navigate('/publish?from=onboarding');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-gray-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        {!isComplete && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-30 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>
        )}

        {!isComplete && (
          <div className="relative z-10 px-8 pt-8 pb-6">
            {/* 標題 */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/20 border border-teal-400/30 rounded-full text-teal-300 text-xs font-bold mb-3">
                <i className="ri-sparkling-2-fill text-amber-400 animate-pulse" />
                屋主引導流程
              </div>
              <h2 className="text-xl font-black text-white">開始刊登您的房源</h2>
              <p className="text-white/50 text-xs mt-1">只需 4 步驟，讓 AI 幫您自動招租</p>
            </div>

            {/* 步驟指示器 */}
            <div className="flex items-center justify-center mb-8">
              {STEPS.map((s, idx) => {
                const isDone = completedSteps.includes(s.id);
                const isCurrent = step === s.id;
                return (
                  <div key={s.id} className="flex items-center">
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 transition-all duration-300 flex-shrink-0 ${
                        isDone
                          ? 'bg-teal-500 border-teal-400/30 shadow-lg shadow-teal-500/30'
                          : isCurrent
                          ? 'bg-white/10 border-teal-400/30 shadow-lg shadow-teal-500/20'
                          : 'bg-white/5 border-white/15'
                      }`}>
                        {isDone
                          ? <i className="ri-check-line text-white text-xs" />
                          : <span className={`text-[11px] font-black ${isCurrent ? 'text-teal-300' : 'text-white/30'}`}>{s.id}</span>
                        }
                      </div>
                      <span className={`text-[9px] font-bold w-12 text-center leading-tight ${
                        isDone ? 'text-teal-400' : isCurrent ? 'text-white/80' : 'text-white/25'
                      }`}>
                        {isDone ? '已完成' : s.label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`w-8 h-0.5 mx-1 mb-5 flex-shrink-0 rounded-full transition-all duration-500 ${
                        completedSteps.includes(s.id) ? 'bg-teal-500' : 'bg-white/10'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>

            {step === 1 && <Step1Content user={user} onNext={() => setStep(2)} />}
            {step === 2 && (
              <Step2Content
                onGoPublish={handleGoPublish}
                onNext={() => { setUploadVisited(true); setStep(3); }}
              />
            )}
            {step === 3 && (
              <Step3Content
                confirmed={botConfirmed}
                onConfirm={() => setBotConfirmed(true)}
                onNext={() => setStep(4)}
              />
            )}
            {step === 4 && (
              <Step4AutoPost
                onClose={onClose}
                onNext={() => setStep(5)}
              />
            )}
          </div>
        )}

        {isComplete && (
          <Step5Complete
            onClose={onClose}
            botConfirmed={botConfirmed}
            autoPostConfirmed={autoPostConfirmed}
            onSetBotConfirmed={setBotConfirmed}
            onSetAutoPostConfirmed={setAutoPostConfirmed}
          />
        )}
      </div>
    </div>
  );
}

/* ──────────────── Step 1 ──────────────── */
function Step1Content({ user, onNext }: { user: { name?: string; email?: string } | null; onNext: () => void }) {
  return (
    <div className="space-y-4">
      <div className="bg-teal-500/10 border border-teal-400/30 rounded-2xl p-5 flex items-center gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-full flex-shrink-0">
          <i className="ri-user-check-fill text-teal-300 text-xl" />
        </div>
        <div>
          <p className="text-teal-300 font-bold text-sm">帳號登入成功！</p>
          <p className="text-white/60 text-xs mt-0.5">
            {user?.name && <span className="text-white/80 font-semibold">{user.name}</span>}
            {user?.email && <span className="ml-1">({user.email})</span>}
          </p>
        </div>
        <div className="ml-auto flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-teal-500">
          <i className="ri-check-line text-white text-xs" />
        </div>
      </div>
      <p className="text-white/50 text-xs text-center">您已成功登入藍舍平台，帳號已安全驗證</p>
      <button
        onClick={onNext}
        className="w-full py-3 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
      >
        下一步：上傳物件 <i className="ri-arrow-right-line" />
      </button>
    </div>
  );
}

/* ──────────────── Step 2 ──────────────── */
function Step2Content({ onGoPublish, onNext }: { onGoPublish: () => void; onNext: () => void }) {
  const uploadSteps = [
    { icon: 'ri-map-pin-2-line', color: 'text-teal-400', bg: 'bg-teal-500/15', text: '填寫房屋地址、坪數、房型、樓層' },
    { icon: 'ri-money-dollar-circle-line', color: 'text-amber-400', bg: 'bg-amber-500/15', text: '設定月租金、押金與入住條件' },
    { icon: 'ri-camera-line', color: 'text-sky-400', bg: 'bg-sky-500/15', text: '上傳房屋照片（建議至少 3 張）' },
    { icon: 'ri-send-plane-line', color: 'text-green-400', bg: 'bg-green-500/15', text: '點擊「發布房源」完成刊登' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-500/15 flex-shrink-0">
            <i className="ri-upload-cloud-2-line text-xl text-amber-300" />
          </div>
          <div>
            <p className="text-white font-bold text-sm">上傳您的房源物件</p>
            <p className="text-white/50 text-xs mt-0.5">只需幾分鐘，AI 幫你自動優化描述</p>
          </div>
        </div>
        <div className="space-y-2.5">
          {uploadSteps.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                <i className={`${s.icon} text-xs ${s.color}`} />
              </div>
              <span className="text-white/70 text-xs">{s.text}</span>
            </div>
          ))}
        </div>
        <div className="flex items-start gap-2.5 bg-teal-500/10 border border-teal-400/20 rounded-xl px-3.5 py-3">
          <i className="ri-sparkling-2-fill text-amber-400 text-sm flex-shrink-0 mt-0.5 animate-pulse" />
          <p className="text-white/60 text-xs leading-relaxed">
            <span className="text-teal-300 font-bold">AI 小幫手：</span>
            上傳後 AI 會自動產生吸睛標題與房源描述，讓您的物件更容易被租客找到！
          </p>
        </div>
      </div>
      <button
        onClick={onGoPublish}
        className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-black rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
      >
        <i className="ri-upload-cloud-2-line text-base" />
        前往上傳房源頁面
        <i className="ri-arrow-right-line" />
      </button>
      <button
        onClick={onNext}
        className="w-full py-2.5 bg-white/6 hover:bg-white/10 border border-white/12 text-white/50 hover:text-white/80 font-medium rounded-xl transition-all text-xs flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
      >
        已完成上傳，繼續下一步
        <i className="ri-arrow-right-line text-xs" />
      </button>
    </div>
  );
}

/* ──────────────── Step 3 ──────────────── */
function Step3Content({
  confirmed,
  onConfirm,
  onNext,
}: {
  confirmed: boolean;
  onConfirm: () => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className={`border rounded-2xl p-5 space-y-3 ${confirmed ? 'bg-teal-500/10 border-teal-400/30' : 'bg-white/5 border-white/10'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 ${confirmed ? 'bg-teal-500/20' : 'bg-green-500/15'}`}>
            <i className={`ri-robot-2-line text-xl ${confirmed ? 'text-teal-300' : 'text-green-300'}`} />
          </div>
          <div>
            <p className={`font-bold text-sm ${confirmed ? 'text-teal-300' : 'text-white'}`}>
              {confirmed ? '已加入官方機器人！' : '加入藍舍官方 LINE 機器人'}
            </p>
            <p className="text-white/50 text-xs mt-0.5">
              {confirmed ? '租客來訊時，AI 將即時通知您的 LINE' : '隨時掌握租客詢問，不錯過任何看房機會'}
            </p>
          </div>
          {confirmed && (
            <div className="ml-auto w-6 h-6 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
              <i className="ri-check-line text-white text-xs" />
            </div>
          )}
        </div>
        {!confirmed && (
          <div className="bg-black/30 rounded-xl p-4 flex flex-col items-center gap-3">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-green-500/20 border border-green-400/30">
              <i className="ri-line-fill text-green-400 text-3xl" />
            </div>
            <div className="text-center">
              <p className="text-white/80 text-sm font-bold">藍舍 AI 管家機器人</p>
              <p className="text-white/40 text-xs mt-1">LINE ID：@blueshelter</p>
            </div>
            <a
              href="https://line.me/R/ti/p/@blueshelter"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-full py-2.5 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap"
              onClick={onConfirm}
            >
              <i className="ri-line-fill text-base" />
              點此加入 LINE 官方帳號
            </a>
          </div>
        )}
      </div>
      {confirmed ? (
        <button
          onClick={onNext}
          className="w-full py-3 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          下一步：智能發文 <i className="ri-arrow-right-line" />
        </button>
      ) : (
        <div className="space-y-2.5">
          <button
            onClick={onConfirm}
            className="w-full py-3 bg-white/8 hover:bg-white/14 border border-white/15 text-white/70 hover:text-white font-bold rounded-xl transition-all text-xs flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-double-line text-teal-400" />
            已加入，繼續下一步
          </button>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/25 text-xs">或</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button
            onClick={onNext}
            className="w-full py-3 bg-white/6 hover:bg-white/12 border border-white/20 hover:border-white/35 text-white/55 hover:text-white/80 font-medium rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap group"
          >
            <i className="ri-skip-forward-line text-white/40 group-hover:text-white/70 text-base" />
            暫時跳過此步驟
          </button>
          <p className="text-center text-white/30 text-xs leading-relaxed px-2">
            跳過後仍可在儀表板的通知設定中隨時加入 LINE 機器人
          </p>
        </div>
      )}
    </div>
  );
}

/* ──────────────── Step 4：智能發文 ──────────────── */
function Step4AutoPost({
  onClose,
  onNext,
}: {
  onClose: () => void;
  onNext: () => void;
}) {
  const navigate = useNavigate();
  const platforms = [
    { icon: 'ri-threads-line', color: 'text-white', bg: 'bg-white/10', name: 'Threads' },
    { icon: 'ri-facebook-circle-line', color: 'text-sky-400', bg: 'bg-sky-500/15', name: 'Facebook' },
    { icon: 'ri-instagram-line', color: 'text-pink-400', bg: 'bg-pink-500/15', name: 'Instagram' },
  ];

  const handleGoAutoPost = () => {
    onClose();
    navigate('/landlord-dashboard?openAiPromo=1');
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-500/15 flex-shrink-0">
            <i className="ri-megaphone-line text-xl text-amber-300" />
          </div>
          <div>
            <p className="font-bold text-sm text-white">AI 智能社群發文</p>
            <p className="text-white/50 text-xs mt-0.5">生成文案並一鍵發送到 Threads 官方帳號</p>
          </div>
        </div>

        {/* 平台列表 */}
        <div className="flex items-center gap-3 justify-center">
          {platforms.map((p, i) => (
            <div key={i} className={`flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 ${p.bg}`}>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${p.icon} text-xl ${p.color}`} />
              </div>
              <span className="text-white/50 text-[10px]">{p.name}</span>
            </div>
          ))}
        </div>

        {/* AI 說明 */}
        <div className="flex items-start gap-2.5 bg-amber-500/8 border border-amber-400/15 rounded-xl px-3.5 py-3">
          <i className="ri-sparkling-2-fill text-amber-400 text-sm flex-shrink-0 mt-0.5 animate-pulse" />
          <p className="text-white/60 text-xs leading-relaxed">
            <span className="text-amber-300 font-bold">AI 自動生成文案：</span>
            進入發文頁面後，AI 會自動撰寫吸睛貼文，並一鍵發送到您的 Threads 官方帳號，讓更多潛在租客看到您的房源。
          </p>
        </div>
      </div>

      <div className="space-y-2.5">
        <button
          onClick={handleGoAutoPost}
          className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-black rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
        >
          <i className="ri-sparkling-2-fill text-base" />
          一鍵讓AI幫你增強曝光度
          <i className="ri-arrow-right-line" />
        </button>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/25 text-xs">或</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <button
          onClick={onNext}
          className="w-full py-3 bg-white/6 hover:bg-white/12 border border-white/20 hover:border-white/35 text-white/55 hover:text-white/80 font-medium rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap group"
        >
          <i className="ri-skip-forward-line text-white/40 group-hover:text-white/70 text-base" />
          稍後再設定
        </button>
        <p className="text-center text-white/30 text-xs leading-relaxed px-2">
          可於儀表板「排程發文」功能中隨時開啟自動發文
        </p>
      </div>
    </div>
  );
}

/* ──────────────── Step 5：完成頁（動態揭示 + 快速補設） ──────────────── */
function Step5Complete({
  onClose,
  botConfirmed,
  autoPostConfirmed,
  onSetBotConfirmed,
  onSetAutoPostConfirmed,
}: {
  onClose: () => void;
  botConfirmed: boolean;
  autoPostConfirmed: boolean;
  onSetBotConfirmed: (v: boolean) => void;
  onSetAutoPostConfirmed: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(0);
  const [localBot, setLocalBot] = useState(botConfirmed);
  const [localPost, setLocalPost] = useState(autoPostConfirmed);

  const handleConfirmBot = () => { setLocalBot(true); onSetBotConfirmed(true); };
  const handleConfirmPost = () => {
    setLocalPost(true);
    onSetAutoPostConfirmed(true);
    onClose();
    navigate('/landlord-dashboard?openSchedule=1');
  };

  const aiFeatures = [
    {
      icon: 'ri-chat-ai-line',
      color: 'text-teal-400',
      bg: 'bg-teal-500/15 border-teal-400/25',
      doneColor: 'bg-teal-500',
      title: 'AI 智能聊天篩選',
      desc: 'AI 已主動與有意看房的租客進行初步詢答，收集居住人數、職業、聯絡電話等資訊，讓您先了解對方再見面。',
      tag: '自動運作中',
      tagColor: 'bg-teal-500/20 text-teal-300',
      done: true,
      action: null,
    },
    {
      icon: 'ri-megaphone-line',
      color: 'text-amber-400',
      bg: 'bg-amber-500/15 border-amber-400/25',
      doneColor: 'bg-amber-500',
      title: '智能社群發文',
      desc: localPost ? 'AI 已開始自動將您的房源排程發布到 Threads、Facebook 等各大社群平台，持續擴大曝光。' : '此功能尚未啟用。點擊右方按鈕立即開啟 AI 自動發文。',
      tag: localPost ? '多平台同步' : '尚未啟用',
      tagColor: localPost ? 'bg-amber-500/20 text-amber-300' : 'bg-white/8 text-white/30',
      done: localPost,
      action: !localPost ? { label: '立即啟動', icon: 'ri-rocket-line', color: 'bg-amber-500 hover:bg-amber-400 text-white', fn: handleConfirmPost } : null,
    },
    {
      icon: 'ri-line-fill',
      color: 'text-green-400',
      bg: 'bg-green-500/15 border-green-400/25',
      doneColor: 'bg-green-500',
      title: 'LINE 即時通知',
      desc: localBot ? '租客資訊彙整後，AI 將完整摘要即時推送到您的 LINE，不必守在電腦前也能掌握看房動態。' : '您尚未加入 LINE 機器人。點擊右方按鈕立即加入，讓 AI 即時通知您。',
      tag: localBot ? '即時推送' : '尚未加入',
      tagColor: localBot ? 'bg-green-500/20 text-green-300' : 'bg-white/8 text-white/30',
      done: localBot,
      action: !localBot
        ? { label: '加入 LINE', icon: 'ri-line-fill', color: 'bg-green-500 hover:bg-green-400 text-white', fn: handleConfirmBot, href: 'https://line.me/R/ti/p/@blueshelter' }
        : null,
    },
  ];

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    aiFeatures.forEach((_, idx) => {
      timers.push(setTimeout(() => setVisibleCount(idx + 1), 500 + idx * 700));
    });
    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative z-10 px-8 pt-8 pb-8 space-y-6 max-h-[85vh] overflow-y-auto">
      {/* 頂部完成標示 */}
      <div className="text-center space-y-2">
        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full mx-auto mb-3 shadow-lg shadow-teal-500/30">
          <i className="ri-award-fill text-white text-2xl" />
        </div>
        <h2 className="text-2xl font-black text-white">恭喜您已完成上架！</h2>
        <p className="text-white/50 text-sm">藍舍 AI 管家正在 24 小時為您運作</p>
        {/* 步驟完成指示 */}
        <div className="flex items-center justify-center gap-1.5 mt-1 flex-wrap">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex items-center gap-1.5">
              <div className="w-5 h-5 flex items-center justify-center bg-teal-500 rounded-full">
                <i className="ri-check-line text-white text-[10px]" />
              </div>
              <span className="text-teal-400 text-xs font-bold">步驟 {n}</span>
              {n < 4 && <i className="ri-arrow-right-s-line text-white/20 text-xs" />}
            </div>
          ))}
          <div className="ml-1.5 flex items-center gap-1">
            <div className="w-5 h-5 flex items-center justify-center bg-amber-400 rounded-full animate-pulse">
              <i className="ri-star-fill text-white text-[10px]" />
            </div>
            <span className="text-amber-400 text-xs font-bold">完成！</span>
          </div>
        </div>
      </div>

      {/* AI 功能狀態動態揭示 */}
      <div>
        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-sparkling-2-fill text-amber-400" />
          AI 目前正在幫您做的事
        </p>
        <div className="space-y-3">
          {aiFeatures.map((f, i) => (
            <div
              key={i}
              className={`border rounded-xl p-4 flex gap-3.5 transition-all duration-500 ${f.bg} ${
                visibleCount > i
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 pointer-events-none'
              }`}
              style={{ transitionDelay: `0ms` }}
            >
              {/* 左側 icon */}
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-black/20 flex-shrink-0 mt-0.5">
                <i className={`${f.icon} text-lg ${f.color}`} />
              </div>

              {/* 內容 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-white font-bold text-sm">{f.title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${f.tagColor}`}>{f.tag}</span>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">{f.desc}</p>
              </div>

              {/* 右側狀態圖示 */}
              <div className="flex-shrink-0 mt-0.5">
                {f.done ? (
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full ${f.doneColor}`}>
                    <i className="ri-check-line text-white text-sm" />
                  </div>
                ) : (
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/8 border border-white/15">
                    <i className="ri-close-line text-white/30 text-sm" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 關閉按鈕（等 3 項都顯示後才出現） */}
      <div className={`transition-all duration-500 ${visibleCount >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <button
          onClick={onClose}
          className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-black rounded-xl transition-all text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25"
        >
          <i className="ri-home-smile-2-line text-base" />
          返回租出管理頁面
        </button>
      </div>
    </div>
  );
}
